import {
  FormattingRuleOperator,
  type FormattingOperator,
  type QueryBuilderRuleValue,
} from '@ardoq/api-types';
import { isDateRangeReversed } from '@ardoq/date-time';
import { type I18nLocale, t } from '@ardoq/i18n';
import { QueryBuilderFilterType } from '@ardoq/query-builder';

const RANGE_COMPARATORS: FormattingOperator[] = [
  FormattingRuleOperator.BETWEEN,
  FormattingRuleOperator.NOT_BETWEEN,
  FormattingRuleOperator.DATE_BETWEEN,
  FormattingRuleOperator.DATE_NOT_BETWEEN,
  FormattingRuleOperator.DATE_RANGE_BETWEEN,
];

const DATE_FILTER_TYPES: QueryBuilderFilterType[] = [
  QueryBuilderFilterType.DATE,
  QueryBuilderFilterType.DATE_TIME,
  QueryBuilderFilterType.DATE_RANGE,
];

type RangeEnd = string | number | null;

type RangeEndsProperties = {
  i18nLocale: I18nLocale;
  start: RangeEnd;
  end: RangeEnd;
};

const toRangeBound = (bound: RangeEnd): number | null => {
  if (typeof bound === 'number') {
    return Number.isNaN(bound) ? null : bound;
  }

  if (typeof bound !== 'string' || bound.trim() === '') {
    return null;
  }

  const parsed = Number(bound);
  return Number.isNaN(parsed) ? null : parsed;
};

const getReversedNumberRangeWarning = ({
  i18nLocale,
  start,
  end,
}: RangeEndsProperties): string | undefined => {
  const startBound = toRangeBound(start);
  const endBound = toRangeBound(end);

  if (startBound === null || endBound === null || startBound <= endBound) {
    return undefined;
  }

  return t(
    i18nLocale,
    'perspectives:rangeWarnings.reversedNumberRange',
    "The 'from' value must be lower than the 'to' value."
  );
};

const getReversedDateRangeWarning = ({
  i18nLocale,
  start,
  end,
}: RangeEndsProperties): string | undefined => {
  if (
    typeof start !== 'string' ||
    typeof end !== 'string' ||
    !isDateRangeReversed({ start, end })
  ) {
    return undefined;
  }

  return t(
    i18nLocale,
    'perspectives:rangeWarnings.reversedDateRange',
    'The start date must be before the end date.'
  );
};

type ReversedRangeWarningProperties = {
  i18nLocale: I18nLocale;
  filterType: QueryBuilderFilterType;
  comparator: FormattingOperator;
  value: QueryBuilderRuleValue;
};

/** Warns when the ends of a "between" range are the wrong way round. */
export const getReversedRangeWarning = ({
  i18nLocale,
  filterType,
  comparator,
  value,
}: ReversedRangeWarningProperties): string | undefined => {
  if (!RANGE_COMPARATORS.includes(comparator)) {
    return undefined;
  }

  if (!Array.isArray(value) || value.length !== 2) {
    return undefined;
  }

  const [start, end] = value;

  if (filterType === QueryBuilderFilterType.NUMBER) {
    return getReversedNumberRangeWarning({ i18nLocale, start, end });
  }

  if (!DATE_FILTER_TYPES.includes(filterType)) {
    return undefined;
  }

  return getReversedDateRangeWarning({ i18nLocale, start, end });
};
